import { useCallback, useEffect, useState } from 'react';
import { ConfirmModal } from '../components/ConfirmModal';
import { ReservedActionListItem } from '../components/ReservedActionListItem';
import { useDebouncedUrlValue } from '../hooks/useDebouncedUrlValue';
import { useUrlState } from '../hooks/useUrlState';
import type { ReservedAction, ReservedActionKind } from '../lib/api-client';
import { apiClient } from '../lib/api-client';

type SortField = 'scheduled_at' | 'created_at' | 'status';
type SortOrder = 'asc' | 'desc';
type KindFilter = ReservedActionKind | '';

// 1 ページあたりの件数。マジックナンバー化を避けるため定数として定義。
const PAGE_SIZE = 20;
const FIRST_PAGE = 1;
// URL に不正値が入った場合のフォールバック用の許可値。
const KIND_FILTERS: readonly KindFilter[] = ['', 'POST', 'REPOST', 'DELETE_POST'];
const STATUS_FILTERS = ['', 'PENDING', 'EXECUTING', 'COMPLETED', 'FAILED', 'CANCELLED'] as const;
const SORT_FIELDS: readonly SortField[] = ['scheduled_at', 'created_at', 'status'];
const SORT_ORDERS: readonly SortOrder[] = ['asc', 'desc'];

export default function PostsPage() {
  const [actions, setActions] = useState<ReservedAction[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ページ・フィルタ・ソートは URL クエリで管理し、戻る / 進む と URL 共有に対応する。
  // 検索ボックスは DID / handle の部分一致（userFilter）。
  // URL の ?userId= は他ページからの遷移で渡る内部ユーザー ID の完全一致で、別物として扱う。
  const [
    {
      page,
      status: statusFilter,
      kind: kindFilter,
      userFilter,
      userId: userIdFromUrl,
      sortField,
      sortOrder
    },
    setUrlState
  ] = useUrlState(
    {
      page: FIRST_PAGE,
      status: '',
      kind: '' as KindFilter,
      userFilter: '',
      userId: '',
      sortField: 'scheduled_at' as SortField,
      sortOrder: 'desc' as SortOrder
    },
    {
      status: STATUS_FILTERS,
      kind: KIND_FILTERS,
      sortField: SORT_FIELDS,
      sortOrder: SORT_ORDERS
    }
  );

  // 入力中の値はローカルに保持し、デバウンス後に URL へ反映する。
  // 1 文字ごとに履歴が増えないよう置き換えで反映し、ページは先頭へ戻す。
  const commitUserFilter = useCallback(
    (next: string) => setUrlState({ userFilter: next, page: FIRST_PAGE }, { replace: true }),
    [setUrlState]
  );
  const [userFilterInput, setUserFilterInput] = useDebouncedUrlValue(userFilter, commitUserFilter);

  // 予約アクション（予約リポスト / 予約削除）のキャンセル確認モーダル。
  // 失敗時にダイアログを閉じずエラーを表示できるよう、専用の状態として管理する。
  const [cancelAction, setCancelAction] = useState<ReservedAction | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  useEffect(() => {
    void loadReservedActions();
  }, [page, statusFilter, kindFilter, userFilter, userIdFromUrl, sortField, sortOrder]);

  const loadReservedActions = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await apiClient.getReservedActions({
        page,
        limit: PAGE_SIZE,
        status: statusFilter || undefined,
        kind: kindFilter || undefined,
        userId: userIdFromUrl || undefined,
        userFilter: userFilter || undefined,
        sortField,
        sortOrder
      });

      setActions(data.items);
      setTotal(data.pagination.total);
      setTotalPages(data.pagination.totalPages);
    } catch (_err) {
      setError('Failed to load reserved actions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelActionRequest = (action: ReservedAction) => {
    setCancelError('');
    setCancelAction(action);
  };

  const handleCancelActionDismiss = () => {
    if (cancelling) {
      return;
    }
    setCancelAction(null);
    setCancelError('');
  };

  const handleCancelActionConfirm = async () => {
    if (!cancelAction) {
      return;
    }

    setCancelling(true);
    setCancelError('');

    try {
      await apiClient.cancelScheduledAction(cancelAction.id);
      setCancelAction(null);
      await loadReservedActions();
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Failed to cancel action');
    } finally {
      setCancelling(false);
    }
  };

  const handleSort = (field: SortField) => {
    const isSameField = sortField === field;
    const nextOrder: SortOrder = isSameField && sortOrder === 'asc' ? 'desc' : 'asc';

    setUrlState({
      sortField: field,
      sortOrder: isSameField ? nextOrder : 'desc',
      page: FIRST_PAGE
    });
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <span className='ml-1 text-gray-400 dark:text-gray-500'>⇅</span>;
    }
    return <span className='ml-1 dark:text-gray-300'>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div>
      <h1 className='mb-6 text-3xl font-bold text-gray-800 dark:text-white'>Reserved Actions</h1>

      {/* Filters */}
      <div className='mb-6 flex flex-wrap gap-4'>
        <select
          value={kindFilter}
          onChange={e => setUrlState({ kind: e.target.value as KindFilter, page: FIRST_PAGE })}
          className='rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
        >
          <option value=''>All Kinds</option>
          <option value='POST'>Post</option>
          <option value='REPOST'>Repost</option>
          <option value='DELETE_POST'>Delete</option>
        </select>

        <select
          value={statusFilter}
          onChange={e => setUrlState({ status: e.target.value, page: FIRST_PAGE })}
          className='rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white'
        >
          <option value=''>All Statuses</option>
          <option value='PENDING'>Pending</option>
          <option value='EXECUTING'>Executing</option>
          <option value='COMPLETED'>Completed</option>
          <option value='FAILED'>Failed</option>
          <option value='CANCELLED'>Cancelled</option>
        </select>

        <input
          type='text'
          placeholder='Filter by User (DID or Handle)'
          value={userFilterInput}
          onChange={e => setUserFilterInput(e.target.value)}
          className='rounded-md border border-gray-300 bg-white px-3 py-2 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400'
        />
      </div>

      {error && (
        <div className='mb-4 rounded-md bg-red-50 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-200'>
          {error}
        </div>
      )}

      {/* Sort Controls */}
      <div className='mb-4 rounded-lg bg-white p-4 shadow dark:bg-gray-800'>
        <div className='flex flex-wrap gap-3'>
          <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>Sort by:</span>
          <button
            onClick={() => handleSort('scheduled_at')}
            className='flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
          >
            Scheduled <SortIcon field='scheduled_at' />
          </button>
          <button
            onClick={() => handleSort('created_at')}
            className='flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
          >
            Created <SortIcon field='created_at' />
          </button>
          <button
            onClick={() => handleSort('status')}
            className='flex items-center text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
          >
            Status <SortIcon field='status' />
          </button>
        </div>
      </div>

      {/* Reserved Actions List */}
      <div className='rounded-lg bg-white shadow dark:bg-gray-800'>
        {loading ? (
          <div className='p-8 text-center dark:text-gray-300'>Loading...</div>
        ) : actions.length === 0 ? (
          <div className='p-8 text-center text-gray-500 dark:text-gray-400'>
            No reserved actions found
          </div>
        ) : (
          <div className='px-4 pt-4'>
            {actions.map(action => (
              <ReservedActionListItem
                key={action.id}
                action={action}
                showAuthor={true}
                onCancel={handleCancelActionRequest}
                cancellingActionId={cancelling ? (cancelAction?.id ?? null) : null}
              />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='mt-6 flex flex-col items-center gap-3 md:flex-row md:justify-between'>
          <div className='text-sm text-gray-700 dark:text-gray-300'>
            Page {page} of {totalPages} ({total} total)
          </div>
          <div className='flex gap-2'>
            <button
              onClick={() => setUrlState({ page: Math.max(FIRST_PAGE, page - 1) })}
              disabled={page <= FIRST_PAGE}
              className='rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-600'
            >
              Previous
            </button>
            <button
              onClick={() => setUrlState({ page: Math.min(totalPages, page + 1) })}
              disabled={page >= totalPages}
              className='rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:disabled:bg-gray-800 dark:disabled:text-gray-600'
            >
              Next
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!cancelAction}
        title='Stop Reserved Action'
        message={`Are you sure you want to stop this reserved action from executing? This only stops the scheduled execution — the record itself will not be deleted.${
          cancelError ? ` (Error: ${cancelError})` : ''
        }`}
        confirmText='Stop Execution'
        cancelText='Close'
        onConfirm={() => void handleCancelActionConfirm()}
        onCancel={handleCancelActionDismiss}
        variant='danger'
        isLoading={cancelling}
      />
    </div>
  );
}
